import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { OrdersService } from 'src/app/core/services/orders.service';

@Component({
  selector: 'app-order-history-timeline-popup',
  templateUrl: './order-history-timeline-popup.component.html',
  styleUrls: ['./order-history-timeline-popup.component.scss']
})
export class OrderHistoryTimelinePopupComponent implements OnInit {
  @Input() mode: 'order' | 'orderItem' = 'order';
  @Input() targetId: number | null = null;
  @Input() referenceNo = '';

  isLoading = false;
  errorMessage = '';
  events: any[] = [];

  constructor(
    public activeModal: NgbActiveModal,
    private ordersService: OrdersService
  ) { }

  ngOnInit(): void {
    this.loadHistory();
  }

  get title(): string {
    return this.mode === 'orderItem' ? 'Order Item History' : 'Order History';
  }

  get subtitle(): string {
    return this.referenceNo ? `Timeline for ${this.referenceNo}` : 'Timeline of changes';
  }

  get emptyMessage(): string {
    return this.mode === 'orderItem'
      ? 'No order item history found.'
      : 'No order history found.';
  }

  getEventTitle(event: any): string {
    if (this.isRevisionEvent(event)) {
      return String(event?.title || '').trim() || 'Item data updated';
    }
    return this.getStatusLabel(event);
  }

  getEventTimestamp(event: any): any {
    return event?.eventAt || event?.audit?.updatedAt || event?.audit?.createdAt || null;
  }

  getStatusLabel(event: any): string {
    if (!event) {
      return '-';
    }
    const raw = this.mode === 'orderItem'
      ? (event.orderItemStatus || event.statusLabel || '')
      : (event.orderStatus || event.statusLabel || '');
    return this.formatLabel(raw) || '-';
  }

  getStateLabel(event: any): string {
    return this.formatLabel(event?.orderState) || '-';
  }

  getCommentLabel(event: any): string {
    return String(event?.comment || '').trim() || '-';
  }

  getSummaryLabel(event: any): string {
    return String(event?.summary || '').trim() || '-';
  }

  getActorLabel(event: any): string {
    return event?.audit?.updatedByName || event?.audit?.createdByName || '-';
  }

  getFieldChanges(event: any): any[] {
    return Array.isArray(event?.changes) ? event.changes : [];
  }

  hasFieldChanges(event: any): boolean {
    return this.getFieldChanges(event).length > 0;
  }

  isRevisionEvent(event: any): boolean {
    return event?.historyType === 'revision';
  }

  private loadHistory(): void {
    if (!this.targetId) {
      this.errorMessage = 'Invalid history target.';
      return;
    }

    this.isLoading = true;
    const request$ = this.mode === 'orderItem'
      ? this.ordersService.getOrderItemAudit(this.targetId)
      : this.ordersService.getOrderHistory(this.targetId);

    request$.subscribe(
      (res: any) => {
        this.isLoading = false;
        if (res?.isError) {
          this.errorMessage = 'Unable to load history.';
          return;
        }
        const data = res?.data || {};
        this.events = this.mode === 'orderItem'
          ? this.buildOrderItemEvents(data)
          : this.buildOrderEvents(data);
      },
      () => {
        this.isLoading = false;
        this.errorMessage = 'Unable to load history.';
      }
    );
  }

  private formatLabel(value: any): string {
    const raw = String(value || '').trim();
    if (!raw) {
      return '';
    }
    return raw
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private buildOrderEvents(data: any): any[] {
    const rawEvents = data?.orderStatusUpdates || [];
    return Array.isArray(rawEvents) ? rawEvents : [];
  }

  private buildOrderItemEvents(data: any): any[] {
    const statusEvents = Array.isArray(data?.orderItemStatusUpdates)
      ? data.orderItemStatusUpdates.map((event: any) => ({
        ...event,
        historyType: 'status',
        eventAt: event?.audit?.createdAt || event?.audit?.updatedAt || null
      }))
      : [];

    const revisionEvents = Array.isArray(data?.orderItemRevisionUpdates)
      ? data.orderItemRevisionUpdates.map((event: any) => ({
        ...event,
        historyType: 'revision',
        eventAt: event?.audit?.updatedAt || event?.audit?.createdAt || null
      }))
      : [];

    return [...statusEvents, ...revisionEvents].sort((left: any, right: any) => {
      const leftTime = new Date(left?.eventAt || 0).getTime();
      const rightTime = new Date(right?.eventAt || 0).getTime();
      if (leftTime !== rightTime) {
        return rightTime - leftTime;
      }
      return Number(right?.id || 0) - Number(left?.id || 0);
    });
  }
}
